/* Root: providers (query, session, toasts), <html> attributes, and the route table built from features.
   Route order (React Router ranks by specificity, this is for reading):
   - /welcome/*: the welcome feature (public, no shell) when installed, the built-in page otherwise;
   - aliases the API sends people to (/onboarding/age, /sign-in, /reset-password, ...) redirect into it;
   - public pages outside the shell: /f/:slug, /shared/..., /replay/:token, /p/:slug, /share-target, plus any
     feature `publicRoutes`;
   - everything else needs a session and a finished onboarding (RequireAuth), inside the shell unless bare. */
import { lazy, Suspense, useEffect, useMemo, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { ToastProvider } from '../ui';
import { createQueryClient } from '../lib/query';
import type { FeatureDescriptor } from './feature';
import { getFeatures } from './features';
import { ALIAS_PATHS } from './gate';
import { AliasRedirect, CreateHub, FeatureScreen, NotFound, RequireAuth, ScreenLoading, Start, Welcome } from './pages';
import { PublicFormRoute, PublicLayout, ReplayRoute, SharedRoute, ShareTargetRoute, SiteInfoRoute } from './public';
import { SessionProvider, type Session } from './session';
import { Shell } from './Shell';
import { applyRootAttributes, useUi } from './store';

const AskRedirect = lazy(() => import('../features/ask/AskRedirect'));
const Gallery = import.meta.env.DEV ? lazy(() => import('./Gallery')) : null;

function RootAttributes() {
  const theme = useUi((s) => s.theme);
  const density = useUi((s) => s.density);
  const ids = useUi((s) => s.ids);
  useEffect(() => { applyRootAttributes({ theme, density, ids }); }, [theme, density, ids]);
  // Keep theme and density in step with other tabs of the app.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => { if (e.key === 'cx.ui') void useUi.persist.rehydrate(); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  return null;
}

const strip = (route: string) => route.replace(/^\/+|\/+$/g, '');

function DriveAlias() {
  const loc = useLocation();
  return <Navigate to={`/memory/drive${loc.search}`} replace />;
}

export function AppRoutes({ features, shellWidth, widgets }: { features: FeatureDescriptor[]; shellWidth?: number; widgets?: boolean }) {
  const publicFeatures = features.filter((f) => f.public);
  const bareFeatures = features.filter((f) => !f.public && f.bare);
  const shellFeatures = features.filter((f) => !f.public && !f.bare);
  const ownsWelcome = publicFeatures.some((f) => strip(f.route) === 'welcome');
  const featurePublicRoutes = features.flatMap((f) => (f.publicRoutes ?? []).map((r) => ({ ...r, id: `${f.id}:${r.path}` })));
  return (
    <Routes>
      {!ownsWelcome && <Route path="/welcome" element={<Welcome features={features} />} />}
      {ALIAS_PATHS.map((p) => <Route key={p} path={p} element={<AliasRedirect />} />)}
      {Gallery && <Route path="/_ui" element={<Suspense fallback={<ScreenLoading />}><Gallery /></Suspense>} />}
      {publicFeatures.map((f) => <Route key={f.id} path={`${strip(f.route)}/*`} element={<div className="shell"><FeatureScreen feature={f} /></div>} />)}
      <Route path="/f/:slug" element={<PublicFormRoute />} />
      <Route path="/shared/:type/:token" element={<SharedRoute />} />
      <Route path="/shared/:token" element={<SharedRoute />} />
      <Route path="/replay/:token" element={<ReplayRoute />} />
      <Route path="/p/:slug/*" element={<SiteInfoRoute />} />
      <Route path="/share-target" element={<ShareTargetRoute />} />
      {featurePublicRoutes.map((r) => {
        const Screen = r.screen;
        return <Route key={r.id} path={r.path} element={<PublicLayout title={r.title}><Suspense fallback={<ScreenLoading />}><Screen /></Suspense></PublicLayout>} />;
      })}
      <Route element={<RequireAuth />}>
        {bareFeatures.map((f) => <Route key={f.id} path={`${strip(f.route)}/*`} element={<div className="shell"><FeatureScreen feature={f} /></div>} />)}
        <Route element={<Shell features={features} width={shellWidth} widgets={widgets} />}>
          <Route index element={<Start features={features} />} />
          {!shellFeatures.some((f) => strip(f.route) === 'create') && <Route path="/create" element={<CreateHub features={features} />} />}
          {shellFeatures.map((f) => <Route key={f.id} path={`${strip(f.route)}/*`} element={<FeatureScreen feature={f} />} />)}
          {!shellFeatures.some((f) => strip(f.route) === 'drive') && <Route path="/drive/*" element={<DriveAlias />} />}
          {/* Research reports lived at /research/:id; they open in Ask now (features/ask/redirect.ts). */}
          {!shellFeatures.some((f) => strip(f.route) === 'research') && shellFeatures.some((f) => f.id === 'search') && <Route path="/research/*" element={<Suspense fallback={<ScreenLoading />}><AskRedirect /></Suspense>} />}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}

export function Providers({ children, queryClient, session }: { children: ReactNode; queryClient?: QueryClient; session?: Session }) {
  const qc = useMemo(() => queryClient ?? createQueryClient(), [queryClient]);
  return (
    <QueryClientProvider client={qc}>
      <SessionProvider value={session}>
        <ToastProvider>
          <RootAttributes />
          {children}
        </ToastProvider>
      </SessionProvider>
    </QueryClientProvider>
  );
}

export function App() {
  const features = useMemo(() => getFeatures(), []);
  return (
    <Providers>
      <AppRoutes features={features} />
    </Providers>
  );
}
